// src/components/BoardPack/pages/area_chart.jsx

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import AreaChart from "../components/charts/AreaChart";
import { useQueue } from "@/context/QueueContext";
import HeadingComponent from "../components/shared/heading";
import { processParams } from "../utils/paramsHelper";

const AreaChartPage = ({ year = "", month = "", monthNumber = "", contract = "", page = null, isRefresh = false }) => {
    const navigate = useNavigate();
    const { enqueue } = useQueue();

    const [loading, setLoading] = useState(true);
    const [years, setYears] = useState([]);
    const [monthName, setMonthName] = useState("");
    const [error, setError] = useState(null);
    const [chartData, setChartData] = useState([]);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!year || !month || !contract) return;
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        fetchData();
    }, [year, month, contract]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const apiParams = processParams(page?.params || {}, { year, month, contract });


            const response = await enqueue(() => apiCall(
                "GET",
                "/boardpack/transaction-by-duration",
                apiParams,
                navigate,
                false,
                false,
                isRefresh
            ));

            if (response?.success) {
                const processedData = processApiData(response);

                setYears(response?.meta?.years || []);
                setMonthName(response?.meta?.month || "");
                setChartData(processedData);
            } else {
                setError('Failed to fetch data');
            }
        } catch (err) {
            setError(err || 'An error occurred while fetching data');
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const processApiData = (apiResponse) => {
        const allYearsData = apiResponse.data || {};
        const availableYears = Object.keys(allYearsData);

        const timeSlots = Array.from({ length: 24 }, (_, i) => `${i}:00`);
        const kpis = apiResponse.kpis || {};
        const monthName = apiResponse.meta?.month || "";

        return availableYears.map(yr => {
            const monthData = allYearsData[yr]?.[monthName] || {};

            const data = timeSlots.map(time => ({
                time,
                value: Number(monthData[time]?.replace(/,/g, '')) || 0
            }));

            const yearKpis = kpis[yr] || {};
            const metrics = Object.keys(yearKpis).map(key => {
                let val = yearKpis[key];

                if (val === "-" || val === null || val === undefined || val === "") {
                    val = 0;
                }

                return {
                    label: key,
                    value: val
                };
            });

            return { year: yr, data, metrics, monthName };
        });
    };

    if (loading) {
        return <LoadingComponent />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} />;
    }

    const title = page?.title;
    const subtitle = page?.subtitle;
    const yAxisLabel = page?.yAxisLabel;

    const totalCharts = chartData.length;
    const shouldCenterLast = totalCharts % 2 !== 0;

    return (
        <div className="px-4" style={{ alignSelf: "flex-start", marginTop: "-32px" }}>
            <div className="flex flex-col gap-0 mb-0.5">
                {title && <HeadingComponent variant={"main"}>{title}</HeadingComponent>}
                {subtitle && <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>}
            </div>

            <div className="grid grid-cols-2 gap-0">
                {chartData.map((chartInfo, index) => {
                    const isLastChart = index === totalCharts - 1;
                    const isCenteredSingle = shouldCenterLast && isLastChart;

                    if (isCenteredSingle) {
                        return (
                            <div
                                key={`chart-${index}`}
                                className="col-span-2 flex justify-center"
                                style={{ borderTop: "2px solid #ffd700" }}
                            >
                                <div style={{ width: "50%", minWidth: "320px" }}>
                                    <AreaChart
                                        data={chartInfo.data}
                                        metrics={chartInfo.metrics}
                                        mainTitle={`${chartInfo.year} - ${chartInfo.monthName}`}
                                        yAxisLabel={yAxisLabel || "MONTHLY DAILY VOLUMES"}
                                        tableRowLabel={`${chartInfo.year} - ${chartInfo.monthName}`}
                                        sidebarBgColor="var(--border-blue)"
                                        sidebarBorderColor="var(--border-blue)"
                                firstColumnLabel="Time"

                                    />
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div
                            key={`chart-${index}`}
                            style={{
                                borderRight: index % 2 === 0 ? "3.5px solid #ffd700" : "none",
                                borderBottom: "2px solid #ffd700",
                            }}
                        >
                            <AreaChart
                                data={chartInfo.data}
                                metrics={chartInfo.metrics}
                                mainTitle={`${chartInfo.year} - ${chartInfo.monthName}`}
                                yAxisLabel={yAxisLabel || "MONTHLY DAILY VOLUMES"}
                                tableRowLabel={`${chartInfo.year} - ${chartInfo.monthName}`}
                                sidebarBgColor="var(--border-blue)"
                                sidebarBorderColor="var(--border-blue)"
                                firstColumnLabel="Time"
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AreaChartPage;